/* facets.js — stable, no duplicate listeners
   - Single event bindings (guarded)
   - Delegated click handling (works after HTML swaps)
   - Updates grid + toolbar + chips + count in one pass
*/
class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    if (this._ttBound) return;           // ✅ prevent double-binding
    this._ttBound = true;

    this.form = this.querySelector('form');
    if (!this.form) return;

    this._onInput = (e) => {
      // Price facet is Apply-only (typing should NOT auto-submit)
      if (e.target.closest('[data-tt-price]')) return;
      this.submitFromForm();
    };

    this._onClick = (e) => {
      // Price “Apply” (our new button)
      const apply = e.target.closest('[data-tt-price-apply]');
      if (apply) {
        e.preventDefault();
        this.submitFromForm();
        return;
      }

      // Price “Clear” should also apply immediately (optional but feels right)
      const clear = e.target.closest('[data-tt-price-clear]');
      if (clear) {
        e.preventDefault();
        this.submitFromForm();
        return;
      }

      // Remove single chip / Remove all
      const a = e.target.closest('facet-remove a, a[data-facet-remove]');
      if (a) {
        e.preventDefault();
        this.fetchAndRender(a.href);
      }
    };

    // Use the form for delegation so swapped inner HTML still works
    this.form.addEventListener('input', this._onInput, { passive: true });
    this.form.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    // Clean up if element is ever removed (theme editor etc.)
    if (!this._ttBound || !this.form) return;
    this.form.removeEventListener('input', this._onInput);
    this.form.removeEventListener('click', this._onClick);
    this._ttBound = false;
  }

  submitFromForm() {
    const url = this.buildUrlFromForm();
    this.fetchAndRender(url);
  }

  buildUrlFromForm() {
    const fd = new FormData(this.form);

    // Normalize price inputs (digits only)
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((k) => {
      if (fd.has(k)) fd.set(k, String(fd.get(k) || '').replace(/[^\d]/g, ''));
    });

    const qs = new URLSearchParams(fd).toString();
    const base = this.form.action.split('?')[0];
    return qs ? `${base}?${qs}` : base;
  }

  setLoading(isLoading) {
    document.body.classList.toggle('facets-loading', isLoading);
    const grid = document.getElementById('ProductGridContainer');
    if (grid) grid.classList.toggle('loading', isLoading);
  }

  fetchAndRender(url) {
    // Abort any in-flight request (prevents “race” glitches)
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();

    this.setLoading(true);

    fetch(url, { credentials: 'same-origin', signal: this._abort.signal })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.renderFromDoc(doc);
        history.replaceState({}, '', url);
        this.setLoading(false);
        window.dispatchEvent(new Event('resize'));
      })
      .catch((err) => {
        // Ignore abort errors, clear loading for everything else
        if (err?.name !== 'AbortError') this.setLoading(false);
        else this.setLoading(false);
      });
  }

  renderFromDoc(doc) {
    const swap = (id) => {
      const src = doc.getElementById(id);
      const dst = document.getElementById(id);
      if (src && dst) dst.innerHTML = src.innerHTML;
    };

    swap('ProductGridContainer');
    swap('FacetsWrapperDesktop');
    swap('ActiveFacetsBelow');
    swap('ProductCountDesktopInline');
    // ✅ NO re-calling connectedCallback
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
